import DirectionalIcon from "../../features/navigation/DirectionalIcon.tsx";
import renderBreadcrumbItem from "../../features/navigation/renderBreadcrumbItem.tsx";
import { t, useStoreLanguage } from "../../features/store-language/i18n.ts";
import React, { useEffect, useMemo, useState } from "react";
import { Breadcrumb as AntBreadcrumb, Button, Tooltip } from "antd";
import { Question } from "@phosphor-icons/react";
import { useLocation } from "react-router-dom";
import { guideService } from "../../components/Guides/guideService.ts";
import GuidesDrawer from "../../components/Guides/GuidesDrawer.tsx";
import "./Breadcrumb.css";
import "../../components/Guides/Guides.css";
import { registerCurrentBreadcrumb } from "../../components/Designing/Support/supportQuickTicketEvent.ts";

const countInteractiveActions = (node) => {
  let count = 0;
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }

    const type = child.type;
    const props = child.props ?? {};
    const isNativeAction =
      typeof type === "string" && (type === "button" || type === "a");
    const hasModalTrigger =
      typeof props.buttonText === "string"
        ? props.buttonText.trim().length > 0
        : Boolean(props.buttonText);
    const isInteractive =
      isNativeAction ||
      props.to != null ||
      props.href != null ||
      props.onClick != null ||
      props.onChange != null ||
      props.htmlType === "submit" ||
      hasModalTrigger;

    if (isInteractive) {
      count += 1;
      return;
    }

    count += countInteractiveActions(props.children);
  });
  return count;
};

const hasRenderableActions = (node) => {
  if (countInteractiveActions(node) > 0) {
    return true;
  }

  let found = false;
  React.Children.forEach(node, (child) => {
    if (found || child == null || child === false || child === true || child === "") {
      return;
    }

    if (typeof child === "string" || typeof child === "number") {
      found = String(child).trim().length > 0;
      return;
    }

    if (!React.isValidElement(child)) {
      return;
    }

    const type = child.type;
    if (type === React.Fragment || typeof type === "string") {
      found = hasRenderableActions(child.props?.children);
      return;
    }

    found = true;
  });
  return found;
};

// Pages provide semantic path items only. Layout, separators, direction and
// responsive action placement are deliberately owned by this shared component.
// Legacy separator items remain accepted so independently deployed modules do
// not break while using the centralized presentation.
const normalizeBreadcrumbItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(function (item) {
    return item && item.type !== "separator" && item.title != null;
  });
};

const Breadcrumb = (props) => {
  const storeLanguage = useStoreLanguage();
  const location = useLocation();
  const [pageGuides, setPageGuides] = useState([]);
  const [guidesDrawerOpen, setGuidesDrawerOpen] = useState(false);
  const normalizedItems = useMemo(function () {
    return normalizeBreadcrumbItems(props.items);
  }, [props.items]);
  const pageTitle = props.title ?? normalizedItems[0]?.title;
  const trailItems = props.title ? normalizedItems : normalizedItems.slice(1);

  useEffect(() => {
    registerCurrentBreadcrumb(normalizedItems);
  }, [storeLanguage, normalizedItems]);

  useEffect(() => {
    let cancelled = false;
    setPageGuides([]);
    if (props.showGuides === false) {
      return;
    }
    guideService
      .getByPath(location.pathname)
      .then(function (guides) {
        if (!cancelled) {
          setPageGuides(Array.isArray(guides) ? guides : []);
        }
      })
      .catch(function () {
        if (!cancelled) {
          setPageGuides([]);
        }
      });
    return function () {
      cancelled = true;
    };
  }, [storeLanguage, location.pathname, props.showGuides]);

  const actions = props.actions ?? props.buttons;
  const actionCount = countInteractiveActions(actions);
  const showActions = actionCount > 0 || hasRenderableActions(actions);
  const actionsClassName = [
    "erp-breadcrumb__actions",
    actionCount <= 1 ? "erp-breadcrumb__actions--single" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const rootClassName = ["erp-breadcrumb", "subheader-solid", props.className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClassName}>
      <div className="erp-breadcrumb__inner">
        {showActions ? (
          <div className="erp-breadcrumb__actions-region" aria-label={t("عملیات صفحه")}>
            <div className={actionsClassName}>{actions}</div>
          </div>
        ) : null}
        <nav className="erp-breadcrumb__trail" aria-label={t("مسیر صفحه")}>
          <div className="erp-breadcrumb__trail-content">
            {pageTitle != null ? (
              <span className="erp-breadcrumb__page-title">{pageTitle}</span>
            ) : null}
            {pageTitle != null && trailItems.length > 0 ? (
              <span className="erp-breadcrumb__title-separator" aria-hidden="true">:</span>
            ) : null}
            {trailItems.length > 0 ? (
              <AntBreadcrumb
                itemRender={renderBreadcrumbItem}
                separator={
                  <DirectionalIcon
                    direction="forward"
                    variant="chevron"
                    className="erp-breadcrumb__path-separator"
                    size={12}
                  />
                }
                items={trailItems}
              />
            ) : null}
            {pageGuides.length > 0 && (
              <Tooltip title={t("راهنمای این بخش")}>
                <Button
                  type="text"
                  icon={<Question weight="bold" />}
                  onClick={function () {
                    setGuidesDrawerOpen(true);
                  }}
                  className="erp-breadcrumb__guide-button"
                  aria-label={t("راهنمای این بخش")}
                ></Button>
              </Tooltip>
            )}
          </div>
        </nav>
      </div>
      <GuidesDrawer
        open={guidesDrawerOpen}
        onClose={function () {
          setGuidesDrawerOpen(false);
        }}
        guides={pageGuides}
      />
    </div>
  );
};

export default Breadcrumb;
